import {
  ConditionOperatorEnumType,
  isConditionGroup,
  isRelatedResourceCondition,
  relativeDateSchema,
  betweenDateSchema,
  segmentConditionGroupsSchema,
  type ConditionGroup,
  type ConditionGroupItem,
  type ConditionOperator,
  type FieldCondition,
  type RelatedResourceCondition,
  type SegmentConditionGroups,
} from '@reach/shared-types/conditions';
import {
  findRelationshipForStoredCondition,
  type FieldInfo,
  type SegmentSchemaCatalog,
} from '@reach/shared-types/segment-schema-graph';
import { getOperatorsForFieldType } from './field-type-operators';

type TypedFieldType = Exclude<FieldInfo['type'], 'unknown'>;
type TypedField = FieldInfo & { type: TypedFieldType };
type StoredValue = FieldCondition['value'][number];

/** The catalog plus the schema whose fields the conditions at this level refer to. */
interface ValidationScope {
  catalog: SegmentSchemaCatalog;
  fields: FieldInfo[];
  schemaId: string | null;
}

const VALUE_LESS_OPERATORS: ConditionOperator[] = [
  ConditionOperatorEnumType.EXISTS,
  ConditionOperatorEnumType.NOT_EXISTS,
];
const DATE_RANGE_OPERATORS: ConditionOperator[] = [
  ConditionOperatorEnumType.BETWEEN_MONTH_DAY,
  ConditionOperatorEnumType.BETWEEN_MONTH_DAY_YEAR,
];

const hasSingleValue = (condition: FieldCondition) => condition.value.length === 1;

const isNonBlankScalar = (value: StoredValue) =>
  typeof value === 'string'
    ? value.trim().length > 0
    : typeof value === 'number' || typeof value === 'boolean';

const isFiniteNumber = (value: StoredValue) =>
  value !== '' &&
  (typeof value === 'number' || typeof value === 'string') &&
  Number.isFinite(Number(value));

const isValidDateValue = (value: StoredValue, operator: ConditionOperator) => {
  if (DATE_RANGE_OPERATORS.includes(operator)) return betweenDateSchema.safeParse(value).success;
  const isRelativeDate = typeof value === 'object' && !(value instanceof Date);
  if (isRelativeDate) return relativeDateSchema.safeParse(value).success;
  const isDateLike = typeof value === 'string' || value instanceof Date;
  return isDateLike && Number.isFinite(new Date(value).getTime());
};

const VALUE_IS_VALID_FOR_FIELD_TYPE: Record<TypedFieldType, (condition: FieldCondition) => boolean> = {
  boolean: condition =>
    hasSingleValue(condition) && condition.value.every(value => typeof value === 'boolean'),
  date: condition =>
    hasSingleValue(condition) &&
    condition.value.every(value => isValidDateValue(value, condition.operator)),
  number: condition => condition.value.every(isFiniteNumber),
  string: condition => condition.value.every(isNonBlankScalar),
  reference: condition => condition.value.every(isNonBlankScalar),
  array: condition => condition.value.every(isNonBlankScalar),
};

const findTypedFieldMatchingName = (condition: FieldCondition, fields: FieldInfo[]) =>
  fields.find(
    (candidate): candidate is TypedField =>
      candidate.name === condition.field && candidate.type !== 'unknown',
  );

const hasFieldAppropriateOperator = (condition: FieldCondition, field: TypedField) =>
  getOperatorsForFieldType(field.type).includes(condition.operator);

const operatorNeedsNoValue = (condition: FieldCondition) =>
  VALUE_LESS_OPERATORS.includes(condition.operator);

const itemHasValue = (condition: FieldCondition) => condition.value.length > 0;

const valueIsTypeCompatible = (condition: FieldCondition, field: TypedField) =>
  VALUE_IS_VALID_FOR_FIELD_TYPE[field.type](condition);

function isCompleteFieldCondition(condition: FieldCondition, scope: ValidationScope): boolean {
  const field = findTypedFieldMatchingName(condition, scope.fields);
  if (!field) return false;
  return (
    hasFieldAppropriateOperator(condition, field) &&
    (operatorNeedsNoValue(condition) ||
      (itemHasValue(condition) && valueIsTypeCompatible(condition, field)))
  );
}

const relationshipIsAvailable = (condition: RelatedResourceCondition, scope: ValidationScope) => {
  const { catalog, schemaId } = scope;
  if (!schemaId) return false;
  const relationship = findRelationshipForStoredCondition({
    graph: catalog,
    subjectSchemaId: schemaId,
    storedRelatedResource: condition.relatedResource,
  });
  return Boolean(relationship);
};

const countIsNonNegativeInteger = (condition: RelatedResourceCondition) =>
  Number.isInteger(condition.countValue) && condition.countValue >= 0;

function isCompleteRelatedResourceCondition(
  condition: RelatedResourceCondition,
  scope: ValidationScope,
): boolean {
  const relatedSchema = scope.catalog.schemas.find(
    candidate => candidate.id === condition.relatedResource.schemaId,
  );
  if (!relatedSchema) return false;
  const relatedScope = {
    catalog: scope.catalog,
    fields: relatedSchema.fields,
    schemaId: relatedSchema.id,
  };
  return (
    relationshipIsAvailable(condition, scope) &&
    countIsNonNegativeInteger(condition) &&
    condition.conditions.every(child => isCompleteItem(child, relatedScope))
  );
}

function isCompleteConditionGroup(group: ConditionGroup, scope: ValidationScope): boolean {
  return group.conditions.length > 0 && group.conditions.every(child => isCompleteItem(child, scope));
}

function isCompleteItem(item: ConditionGroupItem, scope: ValidationScope): boolean {
  if (isConditionGroup(item)) return isCompleteConditionGroup(item, scope);
  if (isRelatedResourceCondition(item)) return isCompleteRelatedResourceCondition(item, scope);
  return isCompleteFieldCondition(item, scope);
}

export function isSegmentDefinitionComplete(
  groups: SegmentConditionGroups,
  catalog: SegmentSchemaCatalog,
): boolean {
  const contactScope = { catalog, fields: catalog.contactFields, schemaId: catalog.contactSchemaId };
  return (
    groups.length > 0 &&
    segmentConditionGroupsSchema.safeParse(groups).success &&
    groups.every(group => isCompleteConditionGroup(group, contactScope))
  );
}
